import type {
  DisciplineActivationData,
  DisciplineActivationForm,
  DisciplineActivationPayload,
} from './interfaces'
import type { ListActivation } from '@shared/model'

const ACTIVATION_EMPTY_VALUE = '---'

export const getActivationScheduleLines = (activation: DisciplineActivationData | null): string[] =>
  (activation?.schedule ?? []).map(item => `${item.day}: ${item.from} - ${item.to}`)

export const getActivationStartDate = (activation: DisciplineActivationData | null): string =>
  activation?.start_date || ACTIVATION_EMPTY_VALUE

/** The agent's own text, or — until an agent wrote one — the customer's schedule as a starting point. */
export const getActivationAgentScheduleDraft = (activation: DisciplineActivationData | null): string => {
  if (activation?.agent_schedule) return activation.agent_schedule

  return `${getActivationScheduleLines(activation).join('\n')} \nFirst lesson: ${getActivationStartDate(activation)}`
}

/** The goals-flow codes live outside `all`; without them a saved selection would silently lose them. */
export const getActivationExpectationLabels = (expectations: ListActivation['expectations'] | undefined): Record<string, string> => ({
  ...expectations?.all,
  ...expectations?.activation_goals,
})

export const createActivationForm = (activation: DisciplineActivationData | null): DisciplineActivationForm => ({
  expectations: [...(activation?.details?.expectations ?? [])],
  preferences: activation?.details?.preferences ?? '',
  agentSchedule: getActivationAgentScheduleDraft(activation),
})

const haveSameItems = (first: string[], second: string[]): boolean =>
  first.length === second.length && [...first].sort().join('\n') === [...second].sort().join('\n')

/** The stored agent schedule cannot be cleared through the API, so a blank textarea over a stored text is refused. */
export const isActivationAgentScheduleCleared = (
  activation: DisciplineActivationData | null,
  form: DisciplineActivationForm,
): boolean => !!activation?.agent_schedule && !form.agentSchedule.trim()

/**
 * Only what the admin changed: an untouched field must never overwrite the stored value. The final schedule is the
 * exception that legacy had: what the field shows is what Save stores, so the drafted customer schedule is saved the
 * first time, until the agent's own text exists.
 */
export const buildActivationPayload = (
  initial: DisciplineActivationForm,
  form: DisciplineActivationForm,
  storedAgentSchedule: string | null,
): DisciplineActivationPayload => {
  const payload: DisciplineActivationPayload = {}

  if (!haveSameItems(form.expectations, initial.expectations)) payload.expectations = [...form.expectations]
  if (form.preferences !== initial.preferences) payload.preferences = form.preferences.trim() ? form.preferences : null
  if (form.agentSchedule.trim() && form.agentSchedule !== (storedAgentSchedule ?? '')) {
    payload.agent_schedule = form.agentSchedule
  }

  return payload
}
